import React, { useState, useEffect } from 'react';
import { FaShieldAlt, FaEnvelope } from 'react-icons/fa';
import SEO from '../components/SEO';

const LAST_UPDATED = 'February 7, 2026';
const COMPANY = 'Apexcore Lifestyle Pvt Ltd';
const EMAIL = 'support@fitnession.fit';

const sections = [
  { id: 'introduction',       title: '1. Introduction' },
  { id: 'information',        title: '2. Information We Collect' },
  { id: 'how-we-use',         title: '3. How We Use Your Information' },
  { id: 'sharing',            title: '4. Sharing Your Information' },
  { id: 'payment',            title: '5. Payment & Subscription' },
  { id: 'security',           title: '6. Data Security' },
  { id: 'retention',          title: '7. Data Retention' },
  { id: 'your-rights',        title: '8. Your Rights' },
  { id: 'children',           title: '9. Children\'s Privacy' },
  { id: 'third-parties',      title: '10. Third-Party Services' },
  { id: 'international',      title: '11. International Transfers' },
  { id: 'changes',            title: '12. Changes to This Policy' },
  { id: 'contact',            title: '13. Contact Us' },
  { id: 'grievance',          title: '14. Grievance Officer' },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Privacy Policy | Fitnession"
        description="Fitnession Privacy Policy — Learn how we collect, use, and protect your personal information."
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-4">
            <FaShieldAlt className="text-2xl text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Privacy Policy</h1>
          <p className="text-gray-300">{COMPANY} · Last updated: {LAST_UPDATED}</p>
          <p className="text-gray-400 text-sm mt-2">Applies to the Fitnession mobile application and website.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-12">

          {/* Sticky TOC */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
              <nav className="space-y-1">
                {sections.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`block text-sm py-1.5 px-3 rounded-lg transition-colors ${
                      activeSection === s.id
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article className="flex-1 min-w-0">

            {/* 1 */}
            <section id="introduction" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-600 leading-relaxed">
                Welcome to Fitnession ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.
              </p>
              <p className="text-gray-600 leading-relaxed mt-3">
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
              </p>
            </section>

            {/* 2 */}
            <section id="information" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.1 Personal Information You Provide</h3>
              <p className="text-gray-600 mb-2">We collect information that you voluntarily provide when you:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-5">
                <li>Register for an account (name, email, phone number)</li>
                <li>Complete your health profile (age, gender, height, weight, health conditions)</li>
                <li>Use our coaching features</li>
                <li>Make purchases within the app</li>
                <li>Contact our support team</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.2 Health Information</h3>
              <p className="text-gray-600 mb-2">With your consent, we collect health-related information including:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-5">
                <li>Physical measurements (weight, height, body measurements)</li>
                <li>Health conditions (diabetes, PCOD, thyroid, etc.)</li>
                <li>Dietary preferences and restrictions</li>
                <li>Fitness goals and activity levels</li>
                <li>Progress photos (if you choose to upload them)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">2.3 Mobile App Permissions</h3>
              <p className="text-gray-600 mb-3">Our mobile application requests the following permissions to provide our services:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4">
                <li><strong>Camera (android.permission.CAMERA):</strong> To allow you to take profile and progress photos directly within the app. This is optional and only used when you choose to capture photos. We do not access your camera without your explicit action.</li>
                <li><strong>Photo Library (READ_EXTERNAL_STORAGE, WRITE_EXTERNAL_STORAGE):</strong> To allow you to upload photos from your device. Optional — we only access the specific photos you select.</li>
                <li><strong>Internet (INTERNET):</strong> Required to connect to our servers, sync your data, and provide coaching features.</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-2">
                <p className="text-sm text-blue-800">
                  <strong>Important:</strong> All permissions are requested only when needed and with clear explanations. You can revoke permissions at any time through your device settings. If you deny a permission, certain features (like photo uploads) will not be available, but core functionality will still work.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-5">2.4 Automatically Collected Information</h3>
              <p className="text-gray-600 mb-2">When you use our app, we automatically collect:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600">
                <li>Device information (type, operating system, unique identifiers)</li>
                <li>Usage data (features used, time spent, interactions)</li>
                <li>Log data (IP address, browser type, access times)</li>
              </ul>
            </section>

            {/* 3 */}
            <section id="how-we-use" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-600 mb-3">We use your information to:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600">
                <li>Provide personalised diet and workout plans</li>
                <li>Power our health coaching features</li>
                <li>Track your progress and provide insights</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send important updates and notifications</li>
                <li>Improve our services and develop new features</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            {/* 4 */}
            <section id="sharing" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Sharing Your Information</h2>
              <p className="text-gray-600 mb-4">We do not sell your personal information. We may share your information with:</p>
              <div className="space-y-3">
                {[
                  { who: 'Service Providers', why: 'Third-party companies that help us operate our services (cloud hosting, analytics, payment processing).' },
                  { who: 'Legal Requirements', why: 'When required by law or to protect our rights.' },
                  { who: 'Business Transfers', why: 'In connection with a merger, acquisition, or sale of assets.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4">
                    <p className="font-semibold text-gray-800 text-sm mb-1">{item.who}</p>
                    <p className="text-gray-600 text-sm">{item.why}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* 5 */}
            <section id="payment" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment & Subscription Information</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 Google Play In-App Purchases</h3>
              <p className="text-gray-600 mb-3">
                When you purchase a subscription or make in-app purchases through our Android app, payments are processed by Google Play Store. We do not directly collect or store your credit card or payment information.
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-3">
                <li><strong>Purchase Verification:</strong> We verify purchases with Google Play to prevent fraud and ensure legitimate transactions.</li>
                <li><strong>Subscription Management:</strong> We store subscription details (plan type, duration, status) to provide access to premium features.</li>
                <li><strong>Transaction Records:</strong> We maintain records of purchase transactions for support and billing purposes.</li>
                <li><strong>Google Play Privacy:</strong> Payment processing is subject to Google Play's privacy policy.</li>
              </ul>
              <p className="text-gray-600 mb-5">
                Your financial information is handled exclusively by Google Play in accordance with their security standards and privacy policies.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 Subscription Data</h3>
              <p className="text-gray-600 mb-2">We collect and store the following subscription-related information:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600">
                <li>Subscription plan type (Smart or Premium)</li>
                <li>Subscription duration and expiration date</li>
                <li>Purchase verification status</li>
                <li>Transaction identifiers (for support purposes)</li>
              </ul>
            </section>

            {/* 6 */}
            <section id="security" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-600 mb-3">
                We implement appropriate technical and organisational security measures to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Server-side purchase verification to prevent fraud</li>
                <li>Row-level security on databases</li>
                <li>Regular security audits</li>
                <li>Access controls and employee training</li>
              </ul>
              <p className="text-gray-600">
                However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* 7 */}
            <section id="retention" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-600 mb-3">
                We retain your personal information for as long as your account is active or as needed to provide you services. You can request deletion of your account and data at any time by contacting us at {EMAIL}.
              </p>
              <p className="text-gray-600">
                <strong>Subscription Data:</strong> Purchase records and transaction data are retained for 7 years as required by applicable tax and accounting regulations.
              </p>
            </section>

            {/* 8 */}
            <section id="your-rights" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
              <p className="text-gray-600 mb-4">You have the right to:</p>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {[
                  { right: 'Access', desc: 'Request a copy of your personal data.' },
                  { right: 'Correction', desc: 'Request correction of inaccurate data.' },
                  { right: 'Deletion', desc: 'Request deletion of your data.' },
                  { right: 'Portability', desc: 'Request transfer of your data.' },
                  { right: 'Withdraw Consent', desc: 'Withdraw consent for processing at any time.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4">
                    <p className="font-semibold text-gray-800 text-sm mb-1">{item.right}</p>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-gray-600">
                To exercise these rights, please contact us at {EMAIL}.
              </p>
            </section>

            {/* 9 */}
            <section id="children" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-600">
                Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us immediately at {EMAIL}.
              </p>
            </section>

            {/* 10 */}
            <section id="third-parties" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Services</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">10.1 Service Providers</h3>
              <p className="text-gray-600 mb-3">We use trusted third-party service providers to help us operate our services:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-4">
                <li><strong>Supabase:</strong> Cloud database and authentication services</li>
                <li><strong>Google Play Store:</strong> Payment processing for Android in-app purchases</li>
                <li><strong>Anthropic Claude AI:</strong> AI-powered health coaching and recommendations</li>
                <li><strong>Analytics Services:</strong> To understand app usage and improve our services</li>
              </ul>
              <p className="text-gray-600 mb-5">
                These providers have access only to the information necessary to perform their functions and are obligated to protect your information.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">10.2 External Links</h3>
              <p className="text-gray-600">
                Our app may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies.
              </p>
            </section>

            {/* 11 */}
            <section id="international" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-600">
                Your information may be transferred to and processed in countries other than India. We ensure that appropriate safeguards are in place to protect your information in accordance with this privacy policy.
              </p>
            </section>

            {/* 12 */}
            <section id="changes" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
              <p className="text-gray-600">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last updated" date. Material changes will be communicated via email or in-app notification. You are advised to review this privacy policy periodically.
              </p>
            </section>

            {/* 13 */}
            <section id="contact" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <div className="bg-gray-50 rounded-2xl p-6">
                <p className="font-bold text-gray-900 mb-1">{COMPANY}</p>
                <p className="text-gray-500 text-sm mb-4">Privacy & Support enquiries</p>
                <div className="space-y-2 text-sm text-gray-600">
                  <p className="flex items-center gap-2"><FaEnvelope className="text-blue-500" /> {EMAIL}</p>
                  <p>🌐 https://fitnession.fit</p>
                  <p>📍 Pune, Maharashtra, India</p>
                </div>
              </div>
            </section>

            {/* 14 */}
            <section id="grievance" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Grievance Officer</h2>
              <p className="text-gray-600 mb-4">
                In accordance with the Information Technology Act 2000 and rules made thereunder, the contact details of the Grievance Officer are provided below:
              </p>
              <div className="bg-gray-50 rounded-2xl p-6 text-sm text-gray-600 space-y-1.5">
                <p><strong className="text-gray-800">Name:</strong> Fitnession Support Team</p>
                <p className="flex items-center gap-2"><FaEnvelope className="text-blue-500" /> <strong className="text-gray-800">Email:</strong> {EMAIL}</p>
                <p><strong className="text-gray-800">Response Time:</strong> Within 24–48 hours</p>
              </div>
            </section>

          </article>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
